export type FeatureFlagType = "release" | "ops" | "permission" | "experiment";

export interface FeatureFlagConfig {
  flag: string;
  default: boolean;
  type: FeatureFlagType;
  deprecateAfter?: string; // ISO date, e.g. "2026-05-01"
}

/**
 * Config for a numeric (non-boolean) LaunchDarkly flag. Read with
 * `useFeatureFlagNumber`, which falls back to `default` when LD returns a
 * non-finite/absent value. Callers are responsible for clamping to their own
 * valid range.
 */
export interface NumberFeatureFlagConfig {
  flag: string;
  default: number;
  type: FeatureFlagType;
  deprecateAfter?: string;
}

export const FeatureFlag = {
  EnableMcpKeysInterface: { flag: "enable-mcp-keys-interface", default: false, type: "permission" },
  EnableShadowRuns: { flag: "enable-shadow-runs", default: false, type: "release" },
  EnablePlatformAnalytics: { flag: "enable-platform-analytics", default: false, type: "permission" },
  // enable-user-management gates RBAC work whose OpenFGA dependency is currently on hold at
  // Pacific. Kept as a long-lived `permission` flag (no deprecateAfter) so the deprecation
  // lifecycle does not flag it while paused; revisit its type when the OpenFGA work resumes.
  EnableUserManagement: { flag: "enable-user-management", default: false, type: "permission" },
  EnablePersonalOrgs: { flag: "enable-personal-orgs", default: false, type: "release" },
  EnableEvalGoldenSteward: { flag: "enable-eval-golden-steward", default: false, type: "release" },
  // The Core API already restricts onboarding access to @datacom.com-domain users at the auth
  // layer; this flag further narrows UI availability to a smaller subset on top of that. Kept as
  // a long-lived `permission` flag pending a ReBAC-based role gate (see src/onboarding/AGENTS.md).
  EnableOnboarding: { flag: "enable-onboarding", default: false, type: "permission" },
  // Per-feature onboarding tool gates (each also requires EnableOnboarding). Long-lived
  // `permission` toggles so tools can be shown/hidden independently of the parent portal flag.
  // EnableCompanyConfig also covers Bulk create companies (dependent on config templates).
  EnableCompanyConfig: { flag: "enable-company-config", default: false, type: "permission" },
  EnableCompanyConfigRedesign: {
    flag: "enable-company-config-redesign",
    default: false,
    type: "release",
  },
  EnableBulkCreateCompanyRedesign: {
    flag: "enable-bulk-create-company-redesign",
    default: false,
    type: "release",
  },
  // Accepts Word/PDF questionnaires on Bulk create companies alongside CSV,
  // extracting company rows via the Doc2Company agent. Also requires
  // DOC2COMPANY_AGENT_ID / DOC2COMPANY_SERVICE_ACCOUNT_ID to be configured —
  // the flag gates the experience, the env vars gate whether it can work.
  EnableBulkCreateCompanyDocumentUpload: {
    flag: "enable-bulk-create-company-document-upload",
    default: false,
    type: "release",
  },
  EnablePayComponentImport: { flag: "enable-pay-component-import", default: false, type: "permission" },
  EnablePayComponentImportRedesign: {
    flag: "enable-pay-component-import-redesign",
    default: false,
    type: "release",
  },
  EnableWorkPatternImport: { flag: "enable-work-pattern-import", default: false, type: "permission" },
  EnableWorkPatternImportRedesign: {
    flag: "enable-work-pattern-import-redesign",
    default: false,
    type: "release",
  },
  // Temporary gate to switch from the flat validation table to the new clustered report UI (#426).
  // Remove once the clustered report is GA and the old WorkPatternImportTable is deleted.
  EnableWorkPatternClusteredReport: { flag: "enable-work-pattern-clustered-report", default: false, type: "release", deprecateAfter: "2026-11-17" },
  // Temporary gate for Edit in UI on blocking-error rows plus the new edit-modal fields (#435).
  // Remove once Edit in UI on error rows is GA.
  EnableWorkPatternErrorEditInUi: { flag: "enable-work-pattern-error-edit-in-ui", default: false, type: "release", deprecateAfter: "2026-12-15" },
  EnableSplitCostingImport: { flag: "enable-split-costing-import", default: false, type: "permission" },
  EnableClientPayNormalisation: {
    flag: "enable-client-pay-normalisation",
    default: false,
    type: "permission",
  },
  // Ticket-mandated name for the temporary release selector covering the complete
  // single-page Split Costing experience (shell, confirmations, and clustered report).
  EnableSplitCostingClusteredReport: {
    flag: "enable-split-costing-clustered-report",
    default: false,
    type: "release",
  },
  EnableComponentMatchingImport: {
    flag: "enable-component-matching-import",
    default: false,
    type: "permission",
  },
  // Temporary gate for Datapay-aware split-costing validation (#1061): resolves employee
  // costing from Datapay and applies the R-APP-4 over-100% interval sweep on the validate path.
  // When on, the client drops the AC-APP-04 client-side over-100% check (server blocks instead).
  // Remove once the Datapay-aware validation is GA.
  EnableSplitCostingDatapayAwareValidations: {
    flag: "enable-split-costing-datapay-aware-validations",
    default: false,
    type: "release",
    deprecateAfter: "2027-04-01",
  },
  // Deployment-environment ribbon on onboarding routes (`EnvironmentBanner`). Off until
  // enabled in LaunchDarkly per environment or segment.
  EnableOnboardingEnvironmentBanner: {
    flag: "enable-onboarding-environment-banner",
    default: false,
    type: "ops",
  },
  EnableAgenticPlatform: { flag: "enable-agentic-platform", default: false, type: "experiment" },
  // Master cutover gate (LADR-039 / #440): switches the work-patterns, pay-components, and
  // company-shell config-import flows from Track 1 invokeAgent workflows onto the Slice 5
  // workloads (+ entity) REST APIs. split_costing_import stays on Track 1 regardless of this
  // flag (no EntityType mapping). Flip on after Preview smoke (Step 19); deprecateAfter is left
  // unset until GA is scheduled — /create-release sets it (release date + 4 weeks) at that point.
  EnableWorkloadsCutover: { flag: "enable-workloads-cutover", default: false, type: "release" },
} as const satisfies Record<string, FeatureFlagConfig>;

/** Numeric (non-boolean) flags, read via `useFeatureFlagNumber`. */
export const NumberFeatureFlag = {
  PayComponentImportPageSize: {
    flag: "pay-component-import-page-size",
    default: 100,
    type: "ops",
  },
} as const satisfies Record<string, NumberFeatureFlagConfig>;
